import React, { useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { 
  BarChart3, 
  TrendingUp,
  Star
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

// Stat Card Component
const StatCard = ({ label, value, trend, color = 'burgundy' }) => {
  const colorStyles = {
    burgundy: 'border-burgundy-100',
    green: 'border-green-100',
    yellow: 'border-yellow-100',
  };

  return (
    <div className={`bg-white rounded-2xl border ${colorStyles[color]} p-5`}>
      <div className="flex items-start justify-between mb-2">
        <p className="text-4xl font-serif font-bold text-gray-900">{value}</p>
        {trend && (
          <span className="text-xs text-green-600 flex items-center font-medium">
            <TrendingUp className="w-3 h-3 mr-1" />
            {trend}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 uppercase tracking-wide">{label}</p>
    </div>
  );
};

const PerformancePage = () => {
  const { careOfficers, visits, ratings, performance } = useApp();
  const analytics = useMemo(() => {
    const monthStarts = Array.from({ length: 6 }, (_, index) => {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    });
    const monthly = monthStarts.map(start => {
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
      const monthVisits = visits.filter(visit => {
        const value = visit.actualDate || visit.scheduledStart || visit.scheduledDate;
        if (!value) return false;
        const visitDate = new Date(value);
        return visitDate >= start && visitDate < end;
      });
      const monthRatings = ratings.filter(rating => {
        const ratedAt = rating.subscriberRating?.ratedAt || rating.createdAt;
        if (!ratedAt) return false;
        const ratingDate = new Date(ratedAt);
        return ratingDate >= start && ratingDate < end;
      });
      const avgRating = monthRatings.length
        ? monthRatings.reduce((sum, rating) => sum + Number(rating.subscriberRating?.score || 0), 0) / monthRatings.length
        : 0;
      return {
        month: start.toLocaleDateString('en-US', { month: 'short' }),
        visits: monthVisits.length,
        rating: Number(avgRating.toFixed(1)),
      };
    });

    const leaderboard = careOfficers.map(officer => {
      const officerVisits = visits.filter(visit => visit.careOfficerId === officer.id);
      const completedVisits = officerVisits.filter(visit => ['Approved', 'Submitted', 'Flagged', 'Rejected'].includes(visit.status));
      const perf = performance.find(item => item.officerId === officer.id) || {};
      const officerRatings = ratings.filter(rating => rating.careOfficerId === officer.id);
      const rating = officerRatings.length
        ? officerRatings.reduce((sum, item) => sum + Number(item.subscriberRating?.score || 0), 0) / officerRatings.length
        : Number(officer.rating || perf.rating || 0);
      const completion = officerVisits.length
        ? (completedVisits.length / officerVisits.length) * 100
        : Number(officer.completionRate || perf.completionRate || 0);
      return {
        name: `${officer.firstName || ''} ${officer.lastName || ''}`.trim() || 'Care Officer',
        visits: officerVisits.length || Number(officer.totalVisits || perf.totalVisits || 0),
        rating: Number(rating.toFixed(1)),
        completion: Number(completion.toFixed(1)),
      };
    }).sort((a, b) => b.visits - a.visits);

    const allRatings = leaderboard.map(item => item.rating).filter(Boolean);
    const allCompletion = leaderboard.map(item => item.completion).filter(Boolean);
    const completed = visits.filter(visit => ['Approved', 'Submitted', 'Flagged', 'Rejected'].includes(visit.status));
    const avgDuration = completed
      .map(visit => Number(visit.duration || 0))
      .filter(Boolean);
    return {
      monthly,
      leaderboard,
      avgRating: allRatings.length ? (allRatings.reduce((sum, value) => sum + value, 0) / allRatings.length).toFixed(1) : '0.0',
      avgCompletion: allCompletion.length ? `${(allCompletion.reduce((sum, value) => sum + value, 0) / allCompletion.length).toFixed(1)}%` : '0.0%',
      currentMonthVisits: monthly[monthly.length - 1]?.visits || 0,
      avgDuration: avgDuration.length ? `${Math.round(avgDuration.reduce((sum, value) => sum + value, 0) / avgDuration.length)}min` : '0min',
    };
  }, [careOfficers, performance, ratings, visits]);

  return (
    <div className="min-h-screen bg-cream" data-testid="performance-page">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div>
            <h1 className="font-serif text-2xl font-bold text-gray-900">Performance Analytics</h1>
            <p className="text-sm text-gray-500 mt-1">Monitor care officer performance and quality metrics</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <StatCard label="Avg. Rating" value={analytics.avgRating} color="yellow" />
          <StatCard label="Completion Rate" value={analytics.avgCompletion} color="green" />
          <StatCard label="This Month Visits" value={analytics.currentMonthVisits} color="burgundy" />
          <StatCard label="Avg. Duration" value={analytics.avgDuration} color="burgundy" />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Visits Trend */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-serif text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-gray-400" />
                Monthly Visits
              </h3>
            </div>
            <div className="p-5">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px' }}
                    />
                    <Bar dataKey="visits" fill="#eab308" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Rating Trend */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-serif text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Average Rating Trend
              </h3>
            </div>
            <div className="p-5">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                    <YAxis domain={[4, 5]} stroke="#9ca3af" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px' }}
                    />
                    <Line type="monotone" dataKey="rating" stroke="#eab308" strokeWidth={2} dot={{ fill: '#eab308' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Officer Leaderboard */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-serif text-lg font-semibold text-gray-900">Officer Performance Leaderboard</h2>
            <p className="text-sm text-gray-500">Live data from visits and ratings</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Officer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visits</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completion</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {analytics.leaderboard.map((officer, index) => (
                  <tr key={officer.name} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-200 text-gray-700' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">{officer.name}</td>
                    <td className="px-6 py-4 text-gray-600">{officer.visits}</td>
                    <td className="px-6 py-4">
                      <span className="flex items-center gap-1 text-gray-600">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        {officer.rating}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-green-600 font-medium">{officer.completion}%</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        On Track
                      </span>
                    </td>
                  </tr>
                ))}
                {analytics.leaderboard.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                      No performance data is available yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformancePage;
